import { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { node, object, oneOfType, string } from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import { TextField, Toolbar } from '@material-ui/core';
import { map } from 'lodash';

import useStyles from './PageHeaderStyles';
import messageDescriptor from '../../lingui/LinguiPropTypes';
import PageActions from './PageActions';
import BreadCrumb from '../../breadcrumb/BreadCrumb';
import { NEW_DASHBOARD_ID, useDashboardContext } from '../dashboard/DashboardContext';
import useDebounced from '../../../utilities/UseDebounced';

const TITLE_PLACEHOLDER = '...';

export default function PageHeader({ title, breadcrumbs, titleActions, actions, children }) {
    const { editing, loading, titleError, dashboardActions } = useDashboardContext();
    const [titleText, setTitleText] = useState(TITLE_PLACEHOLDER);
    const theme = useTheme();
    const classes = useStyles();
    const { pageId } = useParams();
    const breadcrumbSeparator = ' > ';
    const { crumbs, documentTitle } = useMemo(() => {
        if (breadcrumbs) {
            return {
                crumbs: breadcrumbs,
                documentTitle: map(breadcrumbs, 'text').join(breadcrumbSeparator),
            };
        }

        return {
            crumbs: [{ text: titleText }],
            documentTitle: titleText,
        };
    }, [titleText, breadcrumbs]);

    const titleEditInputRef = useRef(); // the <input> *within* the <TextField>

    const setDashboardTitleRef = useRef();
    setDashboardTitleRef.current = (newTitle) => {
        if (editing && !loading && newTitle !== TITLE_PLACEHOLDER) {
            dashboardActions.setTitle(newTitle);
            if (titleEditInputRef.current) {
                const titleHasFocus = document.activeElement === titleEditInputRef.current;
                if (pageId === NEW_DASHBOARD_ID && !titleHasFocus) {
                    titleEditInputRef.current.focus();
                    titleEditInputRef.current.select();
                }
            }
        }
    };

    const handleTitleChanged = useDebounced(setTitleText, 250);

    useEffect(() => {
        setTitleText(title || (editing ? '' : TITLE_PLACEHOLDER));
    }, [title, editing]);

    useEffect(() => {
        if (documentTitle === TITLE_PLACEHOLDER) return;
        document.title = `${theme.productName}: ${documentTitle}`;
    }, [theme, documentTitle]);

    useEffect(() => {
        setDashboardTitleRef.current(titleText);
    }, [titleText]);

    return (
        <div id="page-header" className={classes.root}>
            <Toolbar className={classes.pageHeader}>
                {editing && titleText !== TITLE_PLACEHOLDER ? (
                    <TextField
                        required
                        fullWidth
                        inputRef={titleEditInputRef}
                        error={!!titleError}
                        helperText={titleError ? `* ${titleError}` : titleError}
                        label={t`Title`}
                        defaultValue={titleText}
                        onChange={(event) => handleTitleChanged(event.target.value)}
                    />
                ) : (
                    <span id="page-title" className={classes.pageTitle}>
                        <span className={classes.pageTitleText}>
                            <BreadCrumb breadCrumbs={crumbs} />
                        </span>
                        {titleActions}
                    </span>
                )}
                {actions || <PageActions pageTitle={title} />}
            </Toolbar>
            {children}
        </div>
    );
}

PageHeader.propTypes = {
    title: oneOfType([messageDescriptor, string, object]),
    titleActions: node,
    children: node,
};

PageHeader.defaultProps = {
    title: undefined,
    titleActions: undefined,
    children: null,
};
